import { useMemo } from "react";
import { useParams } from "@";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch";
import { useWhatsappCampaignQuery } from "../api";
import { recipientColumns } from "../components";
import type { ColumnDef } from "@tanstack/react-table";
import type { CampaignRecipient, RecipientsFilterValues } from "../types";
import { CampaignStatus, defaultFilterValues } from "../types";

interface StatusInfo {
  title: string;
  color: string;
}

const statusMap: Record<CampaignStatus, StatusInfo> = {
  [CampaignStatus.Draft]: { title: "مسودة", color: "default" },
  [CampaignStatus.Processing]: { title: "قيد المعالجة", color: "yellow" },
  [CampaignStatus.Completed]: { title: "مكتمل", color: "green" },
  [CampaignStatus.Failed]: { title: "فشل", color: "red" },
};

export const useWhatsappCampaignShowPage = () => {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useWhatsappCampaignQuery(id);

  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<RecipientsFilterValues>({
    defaultValues: defaultFilterValues,
  });

  // Campaign status info
  const statusInfo = useMemo<StatusInfo | null>(() => {
    if (!data) return null;
    return statusMap[data.status] || { title: "غير معروف", color: "gray" };
  }, [data]);

  // Recipients stats (always from unfiltered data)
  const stats = useMemo(() => {
    const recipients = data?.recipients || [];
    return {
      total: recipients.length,
      sent: recipients.filter((r) => r.status === 1).length,
      failed: recipients.filter((r) => r.status === 2).length,
      pending: recipients.filter((r) => r.status === 0).length,
    };
  }, [data?.recipients]);

  // Filter recipients using appliedValues from URL
  const filteredRecipients = useMemo(() => {
    if (!data?.recipients) return [];
    const { userName, phoneNumber, status, sentAtFrom, sentAtTo } =
      appliedValues;

    return data.recipients.filter((recipient) => {
      // User name filter
      if (
        userName &&
        !recipient.userName?.toLowerCase().includes(userName.toLowerCase())
      ) {
        return false;
      }
      // Phone number filter
      if (phoneNumber && !recipient.phoneNumber?.includes(phoneNumber)) {
        return false;
      }
      // Status filter
      if (status && recipient.status !== Number(status)) {
        return false;
      }
      // Sent at date range filter
      if (sentAtFrom || sentAtTo) {
        const sentAt = recipient.sentAt ? new Date(recipient.sentAt) : null;
        if (!sentAt) return false;
        if (sentAtFrom && sentAt < sentAtFrom) return false;
        if (sentAtTo && sentAt > sentAtTo) return false;
      }
      return true;
    });
  }, [data?.recipients, appliedValues]);

  // Filtered stats (count from filtered data)
  const filteredStats = useMemo(() => {
    return {
      total: filteredRecipients.length,
      sent: filteredRecipients.filter((r) => r.status === 1).length,
      failed: filteredRecipients.filter((r) => r.status === 2).length,
      pending: filteredRecipients.filter((r) => r.status === 0).length,
    };
  }, [filteredRecipients]);

  // Paginated recipients (client-side pagination on filtered data)
  const paginatedRecipients = useMemo(() => {
    const start = (page - 1) * size;
    const end = start + size;
    return filteredRecipients.slice(start, end);
  }, [filteredRecipients, page, size]);

  // Columns
  const columns = useMemo<ColumnDef<CampaignRecipient>[]>(
    () => recipientColumns,
    [],
  );

  return {
    // Data
    campaign: data,
    recipients: paginatedRecipients,
    columns,
    isLoading,
    error,
    statusInfo,
    stats,
    filteredStats,
    // Pagination (for compatibility with existing components)
    pg: { page, size },
    updatePg: (updates: { page?: number; size?: number }) => {
      if (updates.page !== undefined) setPage(updates.page);
      if (updates.size !== undefined) setSize(updates.size);
    },
    // Search (for compatibility with existing components)
    search: { show: showFilters, values: draftValues },
    updateSearch: (updates: {
      show?: boolean;
      values?: RecipientsFilterValues;
    }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof RecipientsFilterValues, value);
        });
      }
    },
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    // Actions
    refetch,
  };
};
